// src/app/modules/demandes/HorsCatalogueManager.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../../RessourcesStyles/HorsCatalogue.css";

import UsersPickerModal from "./UsersPickerModal";
import BulkUsersFieldsModal from "./BulkUsersFieldsModal";

import { fetchVisibleCampagnesRequest } from "../../../../../redux/actions/campagnesActions";
import { fetchDomainesRequest, fetchThemesRequest } from "../../../../../redux/actions/paramDomainesThemesActions";
import { getDirectReportsRequest } from "../../../../../redux/actions/userActions";
import { createDemandeManagerBulkRequest } from "../../../../../redux/actions/demandesActions";

const initial = {
    campagneId: "",
    domaineId: "",
    themeId: "",
    action: "",
    description: "",
};

export default function HorsCatalogueManager({ campagneId, hideCampagneSelect = false }) {
    const dispatch = useDispatch();

    const campagnesState = useSelector((s) => s.campagnes || {});
    const refState = useSelector((s) => s.paramDomainesThemes || {});
    const usersState = useSelector((s) => s.user || {});

    const campagnes = campagnesState.visibleList || [];
    const domaines = refState.domaines || [];
    const themes = refState.themes || [];
    const teamUsers = usersState.directReports || [];

    const uid = sessionStorage.getItem("uid");

    const [form, setForm] = useState(initial);
    const [localCampagneId, setLocalCampagneId] = useState("");
    const campagneIdEff = hideCampagneSelect ? campagneId : localCampagneId;

    const [openPick, setOpenPick] = useState(false);
    const [openFields, setOpenFields] = useState(false);

    const [pickedUsers, setPickedUsers] = useState([]);
    const [pendingTargets, setPendingTargets] = useState([]);

    useEffect(() => {
        dispatch(getDirectReportsRequest(uid));
        dispatch(fetchVisibleCampagnesRequest());
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch, uid]);

    useEffect(() => {
        if (!hideCampagneSelect && !localCampagneId && campagnes?.length) {
            setLocalCampagneId(String(campagnes[0].id));
        }
    }, [hideCampagneSelect, campagnes, localCampagneId]);

    // sync form.campagneId
    useEffect(() => {
        if (campagneIdEff) setForm((s) => ({ ...s, campagneId: String(campagneIdEff) }));
    }, [campagneIdEff]);

    const themesFiltered = useMemo(() => {
        const did = form.domaineId ? Number(form.domaineId) : null;
        if (!did) return themes || [];
        return (themes || []).filter((t) => Number(t?.domaineId || t?.domaine?.id) === did);
    }, [themes, form.domaineId]);

    const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const openSelector = () => {
        if (!campagneIdEff) return Swal.fire({ icon: "warning", text: "Sélectionne une campagne." });
        if (!form.action?.trim()) return Swal.fire({ icon: "warning", text: "Action / Intitulé est obligatoire." });
        setOpenPick(true);
    };

    const onConfirmUsers = (ids) => {
        const uniqueIds = Array.from(new Set(ids || []));
        setPendingTargets(uniqueIds);

        const users = (teamUsers || []).filter((u) => uniqueIds.includes(u.id));
        setPickedUsers(users);

        setOpenPick(false);
        setOpenFields(true);
    };

    const onSubmitFields = (rows) => {
        const payload = {
            campagneId: Number(campagneIdEff),
            type: "HORS_CATALOGUE",

            domaineId: form.domaineId ? Number(form.domaineId) : null,
            themeId: form.themeId ? Number(form.themeId) : null,
            action: form.action.trim(),
            description: form.description?.trim() || null,

            items: (rows || []).map((r) => ({
                targetUserId: Number(r.targetUserId),
                mode: r.mode || null,
                period: r.period || null,
                niveau: r.niveau || null,
                certifiante: !!r.certifiante,
                priorite: r.priorite != null ? Number(r.priorite) : null,
                objectifs: r.objectifs || null,
                objectifs2: r.objectifs2 || null,
                objectifs3: r.objectifs3 || null,
            })),
        };

        dispatch(createDemandeManagerBulkRequest(payload));

        setOpenFields(false);
        setPickedUsers([]);
        setPendingTargets([]);
    };

    return (
        <div className="hc-page" style={{ padding: 0 }}>
            {!hideCampagneSelect && (
                <div className="hc-card">
                    <div className="hc-field">
                        <label>Campagne</label>
                        <select value={campagneIdEff || ""} onChange={(e) => setLocalCampagneId(e.target.value)}>
                            {(campagnes || []).map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.nom || c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            <div className="hc-card">
                <div className="hc-grid-2">
                    <div className="hc-field">
                        <label>Domaine</label>
                        <select
                            value={form.domaineId}
                            onChange={(e) => {
                                update("domaineId", e.target.value);
                                update("themeId", "");
                            }}
                        >
                            <option value="">—</option>
                            {(domaines || []).map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.nom || d.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="hc-field">
                        <label>Thème</label>
                        <select value={form.themeId} onChange={(e) => update("themeId", e.target.value)}>
                            <option value="">—</option>
                            {(themesFiltered || []).map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.nom || t.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="hc-field">
                    <label>Action / Intitulé *</label>
                    <input value={form.action} onChange={(e) => update("action", e.target.value)} />
                </div>

                <div className="hc-field">
                    <label>Description</label>
                    <textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={4} />
                </div>

                <div className="hc-actions">
                    <button className="hc-btn hc-btn-primary" type="button" onClick={openSelector}>
                        Choisir les utilisateurs  & Continuer
                    </button>
                </div>
            </div>

            <UsersPickerModal
                open={openPick}
                onClose={() => setOpenPick(false)}
                users={teamUsers}
                title="Sélectionner des collaborateurs"
                onConfirm={onConfirmUsers}
            />

            <BulkUsersFieldsModal
                open={openFields}
                onClose={() => setOpenFields(false)}
                users={pickedUsers}
                title="Champs par collaborateur (Hors catalogue - Manager)"
                onSubmit={onSubmitFields}
            />
        </div>
    );
}
